import { format, isDate, parseISO } from "date-fns";
import React from "react";

import { faEyeSlash } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

// components
import { Avatar } from "@performyard/py-components";

import Link from "../../../../../../components/Link";
import ProgressBar from "../../../../../../components/ProgressBar";
import Typography from "../../../../../../components/Text";
import TruncatedText from "../../../../../../components/Text/TruncatedText";
import Tooltip from "../../../../../../components/Tooltip";

import { Goal } from "../../../../../../api/services/searchServiceResultTypes/goal";

// utils
import get from "../../../../../../utils/GetNested";
import toEmployeeUrl from "../../../../../../utils/URLs/Employee";

// css
import css from "../GoalsList.module.scss";

export type goalsTableColumns = {
	id: string;
	label: string;
	width?: number;
	isSortable?: boolean;
	mobileViewHideColumn?: number;
	getter: (goal: Goal) => JSX.Element | string;
};

const getGoalsListCols = (isBulkSelecting = false, goalWeightEnabled = false, groupsEnabled = false) => {
	let nameColumnWidth = 470;
	if (goalWeightEnabled) {
		nameColumnWidth -= 72;
	}

	if (groupsEnabled) {
		nameColumnWidth -= 96;
	}

	const cols = [
		{
			id: "name",
			label: "Name",
			isSortable: true,
			width: nameColumnWidth,
			getter: (goal: Goal) => {
				const { name, _id } = goal;
				const canUserNavigateToGoal = goal.permissions.includes("goals:viewDetails");

				let maxWidth = nameColumnWidth - 8;
				if (isBulkSelecting) {
					maxWidth -= 30;
				}

				return (
					<div className={css.row}>
						{canUserNavigateToGoal ? (
							<Link href={`/company/goal/${_id}/`}>
								<TruncatedText
									maxWidth={maxWidth}
									size="paragraph-3"
									color="primary-5"
									weight="regular"
									text={name}
									useTooltip="never"
								/>
							</Link>
						) : (
							<TruncatedText maxWidth={maxWidth} size="paragraph-3" weight="regular" text={name} />
						)}
					</div>
				);
			},
		},
		{
			id: "public",
			label: "",
			isSortable: false,
			width: 24,
			getter: (goal: Goal) => {
				const transparency = get(goal, "transparency", "");

				return (
					<FontAwesomeIcon
						className={transparency === "public" ? css.isPublic : css.notPublic}
						icon={faEyeSlash}
						data-testid={faEyeSlash.iconName}
					/>
				);
			},
		},
		{
			id: "assignee.name",
			label: "Assignee",
			isSortable: true,
			width: 159,
			getter: (goal: Goal) => {
				const assigneeID = get(goal, ["assignee", "_id"], "");
				const assigneeName = get(goal, ["assignee", "name"], "");

				return (
					<div className={css.userBox}>
						<Avatar
							size={20}
							dangerouslySetClassName={css.userAvatar}
							id={assigneeID}
							alt={`${assigneeName}'s avatar`}
						/>
						<div className={css.userName}>
							<Link href={toEmployeeUrl(assigneeID)} forceHardReload={true}>
								<TruncatedText
									maxWidth={129}
									size="paragraph-3"
									color="primary-5"
									weight="regular"
									text={assigneeName}
								/>
							</Link>
						</div>
					</div>
				);
			},
		},
		{
			id: "end_date",
			label: "Due Date",
			isSortable: true,
			width: 104,
			getter: (goal: Goal) => {
				const dueDate = get(goal, "endDate", "");

				if (dueDate) {
					const parsedDate = parseISO(dueDate);
					return isDate(parsedDate) && format(parsedDate, "MMM d, yyyy");
				}
			},
		},
		{
			id: "status",
			label: "Status",
			isSortable: true,
			width: 72,
			getter: (goal: Goal) => {
				const statusData = get(goal, "status", "");
				const status = statusData === "done" ? "completed" : statusData;

				return status.charAt(0).toUpperCase() + status.slice(1);
			},
		},
		{
			id: "progress",
			label: "Progress",
			isSortable: true,
			width: 160,
			getter: (goal: Goal) => {
				const progress = get(goal, "progress", 0);
				const color = get(goal, ["outlook", "color"], null);
				const needTooltip = progress < -9.99 || progress > 9.99;

				return (
					<div className={css.progress}>
						<ProgressBar
							progress={progress * 100}
							width={120}
							showAmount={false}
							striped={false}
							animated={false}
							color={color}
						/>
						<Tooltip message={`${progress * 100}%`} disabled={!needTooltip}>
							<Typography element="span" weight="regular" size="paragraph-3">
								<div style={{ color: color }}>
									{needTooltip ? `${progress < 9.99 ? "<-" : ">"}999` : Math.floor(progress * 100)}%
								</div>
							</Typography>
						</Tooltip>
					</div>
				);
			},
		},
	];

	if (groupsEnabled) {
		const assigneeColumnIndex = cols.findIndex((col) => col.id === "assignee.name");
		cols.splice(assigneeColumnIndex, 0, {
			id: "assignee.group.name",
			label: "Group",
			isSortable: true,
			width: 96,
			getter: (goal: Goal) => {
				const group = goal?.assignee?.group?.name;

				return (
					<TruncatedText
						maxWidth={92}
						size="paragraph-3"
						color="neutral-9"
						weight="regular"
						text={String(group ?? "")}
					/>
				);
			},
		});
	}

	if (goalWeightEnabled) {
		const dueDateColumnIndex = cols.findIndex((col) => col.id === "end_date");
		cols.splice(dueDateColumnIndex, 0, {
			id: "goal_weight",
			label: "Weight",
			isSortable: true,
			width: 72,
			getter: (goal: Goal) => {
				const goalWeight = goal?.goal_weight;

				return (
					<TruncatedText
						maxWidth={64}
						size="paragraph-3"
						color="neutral-9"
						weight="regular"
						text={String(goalWeight ?? "")}
					/>
				);
			},
		});
	}

	return cols;
};

export default getGoalsListCols;
